import * as React from "react";
import type { Environment } from "../types";

/**
 * S3(CDN)에 올라간 관심사별 설정 JSON(endpoints.json / apps.json 등)을
 * 가져오는 공용 레이어. CDN JSON이 단일 소스다 — 하드코딩 폴백 없음.
 *
 * - 성공 응답은 파일당 세션 내 1회만 fetch(메모이즈). 실패는 캐시하지
 *   않으므로 다음 호출(react-query retry 등)에서 재시도된다.
 * - fetch 실패 시 `getApiBase`는 throw, `useRemoteConfigValue`는 undefined —
 *   호출부는 로딩/에러 상태로 처리한다.
 * - env별 값은 app-launcher-v2.json 컨벤션을 따른다:
 *   값이 env별로 다르면 `{ dev, stage, production }` 객체, 동일하면 문자열.
 */

/** 설정 JSON이 올라가는 CDN 베이스. dev/stage는 staging CDN을 공유한다. */
const REMOTE_CONFIG_BASES: Record<Environment, string> = {
  dev: "https://stg-contents.crosstoken.io/frontend/common/app-launcher",
  stage: "https://stg-contents.crosstoken.io/frontend/common/app-launcher",
  production: "https://contents.crosstoken.io/frontend/common/app-launcher",
};

const FETCH_TIMEOUT_MS = 3_000;

/** env별로 다르면 객체, 모든 env 동일하면 T 단일 값. */
export type RemoteEnvValue<T> = T | Record<Environment, T>;

export function resolveEnvValue<T>(
  value: RemoteEnvValue<T> | null | undefined,
  env: Environment,
): T | undefined {
  if (value == null) return undefined;
  if (
    typeof value === "object" &&
    !Array.isArray(value) &&
    ("dev" in value || "stage" in value || "production" in value)
  ) {
    return (value as Record<Environment, T>)[env];
  }
  return value as T;
}

const configCache = new Map<string, Promise<unknown>>();

/**
 * `<CDN base>/<file>`을 fetch해 JSON으로 반환. 실패 시 null.
 * 성공 결과만 URL 단위로 메모이즈한다 — 실패는 캐시하지 않으므로
 * 다음 호출에서 재시도된다.
 */
export function fetchRemoteConfig<T>(
  file: string,
  env: Environment,
): Promise<T | null> {
  const url = `${REMOTE_CONFIG_BASES[env]}/${file}`;
  let cached = configCache.get(url);
  if (!cached) {
    cached = (async () => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
      try {
        const res = await fetch(url, {
          cache: "no-store",
          signal: controller.signal,
        });
        if (!res.ok) return null;
        return (await res.json()) as unknown;
      } catch {
        return null;
      } finally {
        clearTimeout(timer);
      }
    })().then((result) => {
      if (result === null) configCache.delete(url);
      return result;
    });
    configCache.set(url, cached);
  }
  return cached as Promise<T | null>;
}

/** 테스트 전용 — fetch 메모이즈 캐시 초기화. */
export function __clearRemoteConfigCacheForTest(): void {
  configCache.clear();
}

// ---------------------------------------------------------------------------
// endpoints.json — 백엔드 API base URL
// ---------------------------------------------------------------------------

export type RemoteApiName =
  | "walletServer"
  | "bondingCurve"
  | "posa"
  | "gameReward"
  | "gameSwap";

type EndpointsConfig = {
  apis?: Partial<Record<RemoteApiName, RemoteEnvValue<string>>>;
};

function isSafeBaseUrl(value: unknown): value is string {
  return typeof value === "string" && value.startsWith("https://");
}

/**
 * endpoints.json의 API base를 반환한다. CDN 값이 단일 소스 — fetch 실패
 * 또는 키 누락/비https 값이면 throw하고, react-query retry로 재시도된다.
 * 쿼리 훅의 queryFn 안에서 `await getApiBase("posa", env)` 형태로 사용한다.
 */
export async function getApiBase(
  name: RemoteApiName,
  env: Environment,
): Promise<string> {
  const config = await fetchRemoteConfig<EndpointsConfig>(
    "endpoints.json",
    env,
  );
  const remote = resolveEnvValue(config?.apis?.[name], env);
  if (!isSafeBaseUrl(remote)) {
    throw new Error(
      `[remote-config] endpoints.json missing or invalid api base: ${name} (${env})`,
    );
  }
  return remote;
}

// ---------------------------------------------------------------------------
// apps.json — 앱/서비스 링크 (app-launcher JSON에 없는 것만)
// ---------------------------------------------------------------------------

export type AppsConfig = {
  skills?: RemoteEnvValue<string>;
  gametokenBridge?: RemoteEnvValue<string>;
  /** 브릿지 버튼을 새창 대신 같은 창으로 여는 gametoken 사이트 URL prefix 목록. */
  gametokenSites?: string[];
  bridgePoweredBy?: { url?: RemoteEnvValue<string> };
  /**
   * Get ONEUSD chooser action visibility. Swap/Bridge default to enabled;
   * Transfer Crypto defaults to disabled when remote config is unavailable.
   */
  getOneUsd?: {
    modes?: {
      swap?: boolean;
      bridge?: boolean;
      transferCrypto?: boolean;
    };
  };
  /**
   * 토큰 상세화면 섹션별 노출 여부. 값이 없으면 기본값(history·balance만 노출).
   * 섹션 키: chart / balance(=My Balance + Send) / overview / social / history.
   * env별 값은 apps.json 파일 자체가 환경마다 분리돼 있으므로 단순 boolean만 받는다.
   */
  tokenDetail?: {
    sections?: {
      chart?: boolean;
      balance?: boolean;
      overview?: boolean;
      social?: boolean;
      history?: boolean;
    };
  };
  /**
   * Relay(외부체인 → CROSS 입금) 운영 설정.
   * `minDepositAmount`: 입금 위저드 Continue를 막는 최소 수량(사람 단위,
   * 토큰 공통). CDN 값이 없거나 fetch 실패 시 호출부 폴백(6)이 적용된다 —
   * endpoints.json과 달리 UI 게이트라 폴백을 허용한다.
   */
  relay?: {
    minDepositAmount?: RemoteEnvValue<number | string>;
    /**
     * `maxDepositAmount`: Continue를 막는 최대 수량(사람 단위, 토큰 공통).
     * 브릿지/DEX 유동성 한도를 운영이 수동 반영하는 값 — 미설정 시 상한 없음.
     */
    maxDepositAmount?: RemoteEnvValue<number | string>;
    /**
     * Role-labelled Relay factory addresses. New SDKs prefer this field so
     * operators can tell the StandingForwarderFactory and
     * DepositorForwarderFactory apart without relying on array order.
     *
     * A legacy `trustedFactories` field, if encountered, is deliberately not
     * typed or read by current code.
     */
    forwarderFactories?: RemoteEnvValue<{
      standingForwarderFactory?: string;
      depositorForwarderFactory?: string;
    }>;
  };
};

/**
 * Resolves Relay's role-labelled factory config to the allowlist shape consumed
 * by the verification layer. A legacy `trustedFactories` field is
 * intentionally ignored by current code.
 */
export function resolveRelayForwarderFactories(
  config: AppsConfig | null | undefined,
  env: Environment,
): string[] | undefined {
  const labelled = resolveEnvValue(config?.relay?.forwarderFactories, env);
  const labelledAddresses = [
    labelled?.standingForwarderFactory,
    labelled?.depositorForwarderFactory,
  ].filter((value): value is string => typeof value === "string");

  return labelledAddresses.length > 0 ? labelledAddresses : undefined;
}

// ---------------------------------------------------------------------------
// chains.json — 체인 표시 메타데이터 (표시명·네이티브 심볼)
//
// 체인명·네이티브 심볼은 온체인에서 조회할 수 없는 오프체인 메타데이터다.
// RPC로 얻는 건 chainId 뿐이고, 네이티브 가스 토큰은 컨트랙트가 없어
// symbol()/name()도 없다. 따라서 CDN JSON을 표시용 단일 소스로 둔다.
//
// chainId(10진 문자열)를 키로 매핑한다. 순수 표시(DISPLAY) 전용 —
// 온체인 티커·외부 API 코드(AlchemyPay onramp, 토큰리스트 심볼 비교 등)로
// 재사용하지 말 것. (예: 네이티브 심볼 'ONE'을 잔액매칭 로직에 쓰면 깨진다.)
//
// 예시 chains.json:
//   {
//     "chains": {
//       "612055": { "name": "One Mainnet", "nativeSymbol": "ONE" },
//       "612044": { "name": "One Testnet", "nativeSymbol": "tONE" }
//     }
//   }
// ---------------------------------------------------------------------------

export const CHAINS_CONFIG_FILE = "chains.json";

/** 체인 하나의 표시 메타. 지정 안 된 필드는 호출부 폴백(온체인/하드코딩)으로. */
export type ChainDisplayMeta = {
  /** 체인 표시명 (예: 'One Mainnet'). */
  name?: string;
  /** 네이티브 통화 표시 심볼 (예: 'ONE' / 'tONE'). DISPLAY 전용. */
  nativeSymbol?: string;
};

/** chains.json 스키마 — chainId(10진 문자열) → 표시 메타. */
export type ChainsConfig = {
  chains?: Record<string, ChainDisplayMeta>;
};

/**
 * chains.json에서 특정 chainId의 표시 메타를 비동기로 반환한다.
 * fetch 실패·미정의면 `{}` — 호출부에서 `?? 폴백`으로 처리.
 * React 밖(설정 생성·유틸)에서 사용.
 */
export async function getChainDisplay(
  chainId: number,
  env: Environment,
): Promise<ChainDisplayMeta> {
  const config = await fetchRemoteConfig<ChainsConfig>(CHAINS_CONFIG_FILE, env);
  return config?.chains?.[String(chainId)] ?? {};
}

/**
 * 컴포넌트에서 chainId의 표시명·네이티브 심볼을 구독하는 훅.
 * chainId 변경(네트워크 전환) 시 재조회한다. 로딩 중/미정의면 각 필드
 * undefined — 호출부에서 `?? 온체인/하드코딩 폴백`으로 처리한다.
 */
export function useChainDisplay(
  chainId: number | undefined,
  env: Environment,
): ChainDisplayMeta {
  const [meta, setMeta] = React.useState<ChainDisplayMeta>({});

  React.useEffect(() => {
    if (chainId == null) {
      setMeta({});
      return;
    }
    let alive = true;
    fetchRemoteConfig<ChainsConfig>(CHAINS_CONFIG_FILE, env).then((config) => {
      if (!alive) return;
      setMeta(config?.chains?.[String(chainId)] ?? {});
    });
    return () => {
      alive = false;
    };
  }, [chainId, env]);

  return meta;
}

/**
 * chains.json의 전체 chainId→표시 메타 맵을 구독한다. 여러 체인의 표시명을
 * 한 번에 매핑해야 하는 곳(네트워크 스위처 등)에서 사용한다. 로딩 전/실패 시
 * 빈 객체 — 호출부에서 `map[String(id)]?.name ?? 폴백`으로 처리한다.
 */
export function useChainsConfig(
  env: Environment,
): Record<string, ChainDisplayMeta> {
  const [chains, setChains] = React.useState<Record<string, ChainDisplayMeta>>(
    {},
  );

  React.useEffect(() => {
    let alive = true;
    fetchRemoteConfig<ChainsConfig>(CHAINS_CONFIG_FILE, env).then((config) => {
      if (!alive) return;
      setChains(config?.chains ?? {});
    });
    return () => {
      alive = false;
    };
  }, [env]);

  return chains;
}

/**
 * 컴포넌트에서 원격 설정 값 하나를 구독하는 최소 훅.
 * react-query 의존 없이 동작한다 (QueryClientProvider 없는 소비자도 안전).
 * 로딩 중/실패 시 undefined — 호출부에서 `?? 폴백`으로 처리.
 */
export function useRemoteConfigValue<TConfig, TValue>(
  file: string,
  env: Environment,
  select: (config: TConfig | null) => TValue | undefined,
): TValue | undefined {
  const [value, setValue] = React.useState<TValue | undefined>(undefined);
  const selectRef = React.useRef(select);
  selectRef.current = select;

  React.useEffect(() => {
    let alive = true;
    fetchRemoteConfig<TConfig>(file, env).then((config) => {
      if (!alive) return;
      const next = selectRef.current(config);
      if (next !== undefined) setValue(next);
    });
    return () => {
      alive = false;
    };
  }, [file, env]);

  return value;
}
